import { useEffect, useState } from "react";
import { useSubscription } from "@context/subscription_context";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import { HeartIcon } from "lucide-react";
import { Job } from "@type/job";
import { getListJob, removeShortlistInvite, shortlistInvite } from "@service/job";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { useUserProfileContext } from "@context/user_profile";
import { getCookie, setCookie } from "@util/cookie";
import { Input } from "@component/input";
import { ShortlistedCandidateItem } from "@type/candidate";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    candidate_id: string;
    candidate_name: string;
    selectedJob?: Job | null;
}

export default function ShortlistToInviteDialog(props: Props) {
    const snackbar = useSnackbar();
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    const { isWorkspaceRestricted , maintenanceTooltip} = useSubscription();
    const expiredTooltip = maintenanceTooltip
        ?? (isWorkspaceRestricted
            ? "Your trial has expired. Upgrade to manage favourites."
            : undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedJob, setSelectedJob] = useState<Job | null>(null);
    const [jobs, setJobs] = useState<Job[]>([]);
    const [note, setNote] = useState<string>("");

    useEffect(() => {
        if (props.isOpen) {
            getListJob().then((resp) => {
                if (resp.status === 200) {
                    setJobs(resp.data.data);
                }
            })
        }
    }, [props.isOpen])

    useEffect(() => {
        if (props.selectedJob) {
            const currentShortlistedInfo = shortlistedCookie[props.candidate_id as keyof ShortlistedCandidateItem] || [];
            if (currentShortlistedInfo.length > 0) {
                const existingNote = currentShortlistedInfo.find((item: ShortlistedCandidateItem) => item.job_id === props.selectedJob?.id)?.notes || "";
                if (existingNote !== "") {
                    setNote(existingNote);
                }
            }
            setSelectedJob(props.selectedJob);
        }
    }, [[props.selectedJob]])

    useEffect(() => {
        if (selectedJob && props.candidate_id) {
            const currentShortlistedInfo = shortlistedCookie[props.candidate_id as keyof ShortlistedCandidateItem] || [];
            const existingNote = currentShortlistedInfo.find((item: ShortlistedCandidateItem) => item.job_id === selectedJob.id)?.notes || "";
            setNote(existingNote);
        }
    }, [selectedJob, props.candidate_id])

    const handleShortlist = () => {
        if (selectedJob) {
            setIsLoading(true);
            shortlistInvite({
                job_id: selectedJob.id,
                candidate_id: props.candidate_id,
                notes: note || undefined,
            }).then((resp) => {
                if (resp.status === 200) {
                    snackbar.showSuccess("Candidate marked as favourite.");
                    setCookie(`shortlisted_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                }
            }).catch((error) => {
                snackbar.showError(error?.response?.data?.message || "Failed to mark candidate as favourite.");
                catchErrorLog(`failed to mark candidate as favourite: ${error}`);
            }).finally(() => {
                setIsLoading(false);
                props.onClose();
                setSelectedJob(null);
                setNote("");
            });
        }
    };

    const handleRemoveShortlist = () => {
        if (selectedJob) {
            removeShortlistInvite({
                job_id: selectedJob.id,
                candidate_id: props.candidate_id,
            }).then((resp) => {
                if (resp.status === 200) {
                    snackbar.showSuccess("Candidate removed from favourites.");
                    setCookie(`shortlisted_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                }
            }).catch((error) => {
                snackbar.showError(error?.response?.data?.message || "Failed to shortlist candidate.");
                catchErrorLog(`failed to shortlist candidate: ${error}`);
            }).finally(() => {
                setIsLoading(false);
                props.onClose();
                setSelectedJob(null);
                setNote("");
            });
        }
    }

    const handleCancel = () => {
        // Reset form
        setSelectedJob(null);
        setNote("");
        props.onClose();
    };

    const shortlistedCookie = getCookie(`shortlisted_candidate:${currentUserInfo?.id}`);
    const isShortlisted = shortlistedCookie
        ? shortlistedCookie[props.candidate_id as keyof ShortlistedCandidateItem]
        : false;

    return (
        <Dialog open={props.isOpen} onOpenChange={props.onClose}>
            <DialogContent className="sm:max-w-[500px] [&>*]:min-w-0">
                <DialogHeader>
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                            <HeartIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <DialogTitle className="text-lg font-semibold truncate">
                                {props.selectedJob ? "Mark Applicant as Favourite" : "Mark Candidate as Favourite"}
                            </DialogTitle>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
                                {props.candidate_name}
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    {/* Job Position Selector */}
                    <div className="space-y-2">
                        <Label htmlFor="job-position" className="text-sm font-medium">
                            Job Listing
                        </Label>
                        <Select disabled={!!props.selectedJob} value={selectedJob?.id || ""} onValueChange={(value) => {
                            const job = jobs.find((job) => job.id === value);
                            setSelectedJob(job || null);
                        }}>
                            <SelectTrigger
                                id="job-position"
                                className="w-full min-w-0 overflow-hidden text-left [&>span]:flex-1 [&>span]:min-w-0 [&>span]:truncate [&>span]:text-left"
                            >
                                <SelectValue placeholder="Select a job listing you’re collaborating on" />
                            </SelectTrigger>
                            <SelectContent>
                                {jobs.map((job) => (
                                    <SelectItem key={job.id} value={job.id} className="text-left">
                                        {job.job_title}
                                        {(isShortlisted || []).find((item: ShortlistedCandidateItem) => item.job_id === job.id) && (
                                            <span className="text-xs py-1 px-2 ml-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-full font-medium">
                                                Favourited
                                            </span>
                                        )}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {selectedJob && (
                        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3">
                            <div>
                                <Label className="text-xs font-medium text-muted-foreground mb-1">
                                    Job Title
                                </Label>
                                <Input
                                    value={selectedJob.job_title}
                                    disabled
                                    className="text-sm font-medium bg-white dark:bg-gray-900 min-w-0"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-medium text-muted-foreground mb-1">
                                    Company Name
                                </Label>
                                <Input
                                    value={selectedJob.company_name || ""}
                                    disabled
                                    className="text-sm bg-white dark:bg-gray-900 min-w-0"
                                />
                            </div>
                        </div>
                    )}

                    {/* Optional Note */}
                    <div className="space-y-2">
                        <Label htmlFor="note" className="text-sm font-medium">
                            {!isShortlisted ? "Add a note (optional)" : "Your note"}
                        </Label>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Share your thoughts about this candidate with the team.
                        </p>
                        <Textarea
                            id="note"
                            placeholder="e.g., Strong technical background, great culture fit, impressive portfolio..."
                            value={note}
                            disabled={(isShortlisted || []).find((item: ShortlistedCandidateItem) => item.job_id === selectedJob?.id) ? true : false}
                            onChange={(e) => {
                                setNote(e.target.value);
                            }}
                            className="min-h-[100px] resize-none"
                        />
                    </div>
                </div>

                {/* Warning Message */}
                {!isShortlisted ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                        Please check back your responses, as you may not edit after
                        submitting.
                    </p>
                ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                        Once removed, this shortlist will no longer be visible to your workspace members.
                    </p>
                )}

                {/* Action Buttons */}
                <div
                    className="flex justify-end gap-3 mt-6"
                    style={{ marginTop: "16px" }}
                >
                    <Button variant="outline" disabled={isLoading} onClick={handleCancel}>
                        Cancel
                    </Button>
                    {!isShortlisted && (
                        <Button
                            onClick={handleShortlist}
                            disabled={!selectedJob || isLoading || isWorkspaceRestricted || (isShortlisted || []).find((item: ShortlistedCandidateItem) => item.job_id === selectedJob.id)}
                            title={expiredTooltip}
                            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <HeartIcon className="h-4 w-4" />
                            Mark as Favourite
                        </Button>
                    )}
                    {isShortlisted && (
                        <Button
                            disabled={isLoading || isWorkspaceRestricted}
                            onClick={handleRemoveShortlist}
                            title={expiredTooltip}
                            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Remove Favourite
                        </Button>
                    )}
                </div>

            </DialogContent>
        </Dialog>
    );
}
